import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import { useTranslation } from 'react-i18next';
import type { ArtifactFenceInput } from '@erikdakoda/artifact/shared/artifactFenceSchema';
import ClampedBox from '@erikdakoda/ui/components/ClampedBox';
import DialogBox from '@erikdakoda/ui/components/DialogBox';
import PowerIconButton from '@erikdakoda/ui/components/PowerIconButton';
import { getArtifactPreviewText, shouldTruncateArtifactPreview } from '../shared/artifactRegistry';
import {
  parseArtifactFence,
  serializeArtifactFence,
} from '@erikdakoda/artifact/shared/artifactSyntax';
import ArtifactPreviewCard from './ArtifactPreviewCard';
import ArtifactCopyDownloadButtons from './ArtifactCopyDownloadButtons';
import InlineArtifactCardBody from './InlineArtifactCardBody';
import { useArtifactStore } from '../hooks/useArtifactStore';
import type { EmbeddedArtifactEntry, OpenArtifactHandler } from '@erikdakoda/markdown-ui/types';

type MarkdownArtifactCardProps = {
  /** Raw inner text of the artifact fence (metadata + --- + body). */
  rawSource: string;
  /** Parsed artifact when valid. */
  artifact?: ArtifactFenceInput | null;
  onOpenArtifact?: OpenArtifactHandler;
  activeArtifactIdentifier?: string | null;
  artifactDepth?: number;
  /** When set, saving edited source updates the Slate node. */
  onSaveSource?: (rawSource: string) => void;
  /** Slate void nodes pass contentEditable={false} wrapper attributes. */
  attributes?: React.HTMLAttributes<HTMLDivElement>;
  readOnly?: boolean;
};

function toEntry(artifact: ArtifactFenceInput): EmbeddedArtifactEntry {
  return {
    identifier: artifact.identifier,
    type: artifact.type,
    title: artifact.title,
    content: artifact.content,
    display: artifact.display,
  };
}

/**
 * Compact artifact preview card for markdown read-only and editor contexts.
 */
export default function MarkdownArtifactCard({
  rawSource,
  artifact: parsedArtifact,
  onOpenArtifact,
  activeArtifactIdentifier,
  artifactDepth = 0,
  onSaveSource,
  attributes,
  readOnly = false,
}: MarkdownArtifactCardProps) {
  'use memo';

  const { t } = useTranslation();
  const artifact = parsedArtifact ?? parseArtifactFence(rawSource);
  const artifactIdentifier = artifact?.identifier ?? null;

  const openFullscreen = useArtifactStore((state) => state.openFullscreen);
  const upsertArtifact = useArtifactStore((state) => state.upsertArtifact);
  const bumpContentRevision = useArtifactStore((state) => state.bumpContentRevision);
  const storedArtifact = useArtifactStore((state) =>
    artifactIdentifier ? state.artifacts[artifactIdentifier] : undefined,
  );
  const contentRevision = useArtifactStore((state) =>
    artifactIdentifier ? (state.contentRevision[artifactIdentifier] ?? 0) : 0,
  );
  const [editOpen, setEditOpen] = useState(false);
  const [editSource, setEditSource] = useState(rawSource);
  const resolvedArtifact = artifact
    ? {
        ...artifact,
        content: storedArtifact?.content ?? artifact.content,
        title: storedArtifact?.title ?? artifact.title,
        type: storedArtifact?.type ?? artifact.type,
      }
    : null;

  if (!resolvedArtifact) {
    return (
      <Paper
        className="markdown-artifact-card"
        variant="outlined"
        sx={{ borderColor: 'error.main', my: 1 }}
        {...attributes}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
            {t('artifact-ui:artifactInvalidFence')}
          </Typography>
          <Typography
            variant="caption"
            component="pre"
            sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', m: 0 }}
          >
            {rawSource}
          </Typography>
        </Box>
      </Paper>
    );
  }

  const effectiveDisplay = resolvedArtifact.display ?? 'panel';
  const preview = getArtifactPreviewText(resolvedArtifact.type, resolvedArtifact.content);
  const isActive = activeArtifactIdentifier === resolvedArtifact.identifier;
  const canEditFence = !readOnly && Boolean(onSaveSource);

  const handleCardClick = (): void => {
    if (effectiveDisplay === 'inline') {
      return;
    }
    onOpenArtifact?.(toEntry(resolvedArtifact));
  };

  const handleMaximize = (event: React.MouseEvent): void => {
    event.stopPropagation();
    upsertArtifact(toEntry(resolvedArtifact));
    openFullscreen(resolvedArtifact.identifier);
  };

  const handleRefresh = (event: React.MouseEvent): void => {
    event.stopPropagation();
    bumpContentRevision(resolvedArtifact.identifier);
  };

  const handleEditOpen = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setEditSource(rawSource);
    setEditOpen(true);
  };

  const handleEditSave = (): void => {
    onSaveSource?.(editSource);
    setEditOpen(false);
  };

  const handleToggleDisplay = (event: React.MouseEvent): void => {
    event.stopPropagation();
    if (!onSaveSource) {
      return;
    }
    const nextDisplay = effectiveDisplay === 'inline' ? 'panel' : 'inline';
    onSaveSource(
      serializeArtifactFence({
        ...resolvedArtifact,
        display: nextDisplay,
      }),
    );
  };

  const renderBody = (): React.ReactNode => {
    if (effectiveDisplay === 'inline') {
      return (
        <InlineArtifactCardBody
          type={resolvedArtifact.type}
          content={resolvedArtifact.content}
          title={resolvedArtifact.title}
          artifactDepth={artifactDepth}
          onOpenArtifact={onOpenArtifact}
          contentReloadKey={contentRevision}
        />
      );
    }

    return (
      <ClampedBox lines={2} sx={{ paddingX: 3, paddingY: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            fontFamily: 'monospace',
            lineHeight: 1.4,
          }}
        >
          {preview}
          {shouldTruncateArtifactPreview(resolvedArtifact.type, resolvedArtifact.content)
            ? '…'
            : ''}
        </Typography>
      </ClampedBox>
    );
  };

  return (
    <>
      <ArtifactPreviewCard
        className="markdown-artifact-card"
        artifactType={resolvedArtifact.type}
        title={resolvedArtifact.title}
        isActive={isActive}
        onClick={effectiveDisplay === 'panel' && onOpenArtifact ? handleCardClick : undefined}
        attributes={attributes}
        headerActions={
          <>
            {effectiveDisplay === 'panel' ? (
              <PowerIconButton
                padding={2}
                sx={{ my: -2 }}
                size="small"
                icon="maximize"
                title={t('artifact-ui:artifactOpenFullscreen')}
                onClick={handleMaximize}
              />
            ) : null}
            <PowerIconButton
              padding={2}
              sx={{ my: -2 }}
              size="small"
              icon="arrow-rotate-right"
              title={t('artifact-ui:artifactRefresh')}
              onClick={handleRefresh}
            />
            {!readOnly && onSaveSource ? (
              <PowerIconButton
                padding={2}
                sx={{ my: -2 }}
                size="small"
                icon="pen-to-square"
                title={t('artifact-ui:artifactEditSource')}
                onClick={handleEditOpen}
              />
            ) : null}
            <ArtifactCopyDownloadButtons
              type={resolvedArtifact.type}
              content={resolvedArtifact.content}
              title={resolvedArtifact.title}
            />
            {canEditFence ? (
              <PowerIconButton
                padding={2}
                sx={{ my: -2 }}
                size="small"
                icon={effectiveDisplay === 'inline' ? 'xmark' : 'square-arrow-down-left'}
                title={
                  effectiveDisplay === 'inline'
                    ? t('artifact-ui:artifactHideInline')
                    : t('artifact-ui:artifactShowInline')
                }
                onClick={handleToggleDisplay}
              />
            ) : null}
          </>
        }
      >
        {renderBody()}
      </ArtifactPreviewCard>

      <DialogBox
        isOpen={editOpen}
        title={t('artifact-ui:artifactEditSourceDialogTitle')}
        onConfirm={handleEditSave}
        onCancel={() => setEditOpen(false)}
        cancelLabel={t('artifact-ui:cancel')}
        confirmLabel={t('artifact-ui:save')}
        maxWidth="md"
        fullWidth
      >
        <InputBase
          fullWidth
          multiline
          minRows={12}
          value={editSource}
          onChange={(event) => setEditSource(event.target.value)}
          sx={{
            fontFamily: 'monospace',
            fontSize: 13,
            lineHeight: 1.5,
            width: '100%',
          }}
        />
      </DialogBox>
    </>
  );
}

export { serializeArtifactFence, parseArtifactFence };
